import Image from "next/image";
import type { ComponentProps } from "react";
import { useCallback, useMemo } from "react";

import type { FragmentType } from "../../../../../__generated__/gql";
import { graphql, useFragment } from "../../../../../__generated__/gql";
import { AccommodationReservationProviderType } from "../../../../../__generated__/gql/graphql";
import { useInNativeApp } from "../../../../_hooks/use-in-native-app";
import { postMessageToNativeApp } from "../../../../utils/native-app";
import { useReservationFlowOverride } from "../reservation-flow-override-context";

import { Accommodations } from "./accommodations";

const Fragment = graphql(`
  fragment AccommodationsGroupOnCalendar on Accommodation {
    id
    name
    isOwned(provider: $provider)
    roomType {
      slug
    }
    site {
      slug
      area {
        slug
      }
    }
    ...AccommodationsOnCalendar
  }
`);

function buildSiteDetailPath(accommodation: {
  site: { slug: string; area: { slug: string } };
}): string {
  return `/areas/${accommodation.site.area.slug}/sites/${accommodation.site.slug}`;
}

// RoomType.slug があれば部屋タイプ詳細、 無ければ拠点詳細ページのパスを返す。
// site / area の slug は常に存在するため、 必ず有効な公開ページに着地する。
function buildAccommodationDetailPath(accommodation: {
  roomType?: { slug?: string | null } | null;
  site: { slug: string; area: { slug: string } };
}): string {
  const roomTypeSlug = accommodation.roomType?.slug;
  if (!roomTypeSlug) return buildSiteDetailPath(accommodation);
  return `${buildSiteDetailPath(accommodation)}/room-types/${roomTypeSlug}`;
}

// 自己所有物件は部屋のセクションを分けて表示する
export function AccommodationsGroup(
  props: Omit<
    ComponentProps<typeof Accommodations>,
    "accommodationsData" | "onAccommodationClick"
  > & {
    accommodationsData: FragmentType<typeof Fragment>[];
    providerType?: AccommodationReservationProviderType;
  },
) {
  const accommodations = useFragment(Fragment, props.accommodationsData);
  const flowOverride = useReservationFlowOverride();
  const isInNativeApp = useInNativeApp();
  const ownedAccommodations = useMemo(
    () => accommodations.filter((accommodation) => accommodation.isOwned),
    [accommodations],
  );
  const notOwnedAccommodations = useMemo(
    () => accommodations.filter((accommodation) => !accommodation.isOwned),
    [accommodations],
  );
  const hasOwned = ownedAccommodations.length > 0;
  const hasNotOwned = notOwnedAccommodations.length > 0;

  const accommodationsById = useMemo(
    () => new Map(accommodations.map((a) => [a.id, a] as const)),
    [accommodations],
  );

  const handleAccommodationClick = useCallback(
    (accommodationId: string) => {
      if (flowOverride) {
        flowOverride.onAccommodationSelected(accommodationId);
        return;
      }
      // 部屋番号タップ: 部屋タイプ詳細 (slug 無しなら拠点詳細) ページを別タブで開く。
      const accommodation = accommodationsById.get(accommodationId);
      if (!accommodation) return;
      // 部屋タイプ詳細 (slug 無しなら拠点詳細) を開く。 ArchitectureInfo モーダルは廃止し、
      // slug 未設定でも拠点詳細ページに着地させる (DXPJ-82 follow-up)。
      const detailPath = buildAccommodationDetailPath(accommodation);
      // nativeapp の WebView 内では window.open("_blank") が機能しないため、
      // postMessage でネイティブに委譲し WebView 画面を modal で開く。
      // タイトルには部屋番号（accommodation.name, 例: "0102"）を表示する。
      if (isInNativeApp) {
        postMessageToNativeApp(
          {
            type: "OpenInWebView",
            payload: {
              url: `${window.location.origin}${detailPath}`,
              presentation: "modal",
              title: accommodation.name,
            },
          },
          window,
        );
      } else {
        window.open(detailPath, "_blank");
      }
    },
    [accommodationsById, flowOverride, isInNativeApp],
  );

  return (
    <>
      {hasOwned &&
        props.providerType &&
        isOwnerProviderType(props.providerType) && (
          <OwnerHeader providerType={props.providerType} />
        )}
      {hasOwned && (
        <Accommodations
          {...props}
          accommodationsData={ownedAccommodations}
          onAccommodationClick={handleAccommodationClick}
        />
      )}
      {hasOwned && hasNotOwned && <NotOwnerHeader />}
      {hasNotOwned && (
        <Accommodations
          {...props}
          accommodationsData={notOwnedAccommodations}
          onAccommodationClick={handleAccommodationClick}
        />
      )}
    </>
  );
}

function OwnerHeader({
  providerType,
}: {
  providerType: AccommodationReservationProviderType;
}) {
  return (
    <div>
      <div className="bg-sorange relative flex items-center justify-between px-[20px] py-[8px] text-white">
        <div className="flex items-center gap-[5px]">
          <span className="font-roboto text-[14px] font-bold">
            {ownerLabel(providerType)}
          </span>
          <span className="font-roboto text-[11px] font-bold">所有物件</span>
        </div>
        <Image
          src="/i/reservation_form_site_arrow_icon_orange.svg"
          width={10}
          height={5}
          alt="down"
          className="absolute bottom-[-5px] left-[27px] z-1"
        />
      </div>
    </div>
  );
}

function NotOwnerHeader() {
  return (
    <div className="bg-skogecha-92 relative flex items-center justify-between px-[20px] py-[8px] text-white">
      <div className="bg-skogecha-100 font-roboto rounded-[2px] p-[6px] text-[11px] font-bold">
        Not-owned
      </div>
      <Image
        src="/i/reservation_form_site_arrow_icon.svg"
        width={10}
        height={5}
        alt="down"
        className="absolute bottom-[-5px] left-[27px] z-1"
      />
    </div>
  );
}

function isOwnerProviderType(
  type: AccommodationReservationProviderType,
): boolean {
  return (
    type === AccommodationReservationProviderType.FullOwnerPlan ||
    type === AccommodationReservationProviderType.CoOwnersPlan
  );
}

function ownerLabel(
  type: AccommodationReservationProviderType,
): "Owners" | "Co-Owners" {
  switch (type) {
    case AccommodationReservationProviderType.FullOwnerPlan:
      return "Owners";
    case AccommodationReservationProviderType.CoOwnersPlan:
      return "Co-Owners";
    default:
      throw new Error("Invalid provider type");
  }
}
